import { ReactNode, useState } from 'react'

import {
  useTagDeliveryGetApi,
  useTagDeliveryTokenIssueApi,
  useTagDeliveryUpdateApi
} from '@/common/api/modules/searchTag'
import { MeasurementStatus, TagEndpointStatus } from '@/common/api/params/searchTag'
import { siteEditPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import CheckBox, { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

const MEASUREMENT_TEXT: Record<MeasurementStatus, string> = {
  active: '受信中',
  stale: '受信が止まっています',
  no_data: '未受信'
}

const dt = (v: string | null) => (v ? new Date(v).toLocaleString('ja-JP') : null)

const CopyBlock = ({ text, label }: { text: string; label?: string }) => {
  const [copied, setCopied] = useState(false)
  return (
    <div>
      {label && (
        <Text size={12} weight='bold'>
          {label}
        </Text>
      )}
      <pre className='bg-fill-surface-tertiary border-border-input mt-1 overflow-x-auto rounded-lg border p-4 text-[12px] leading-relaxed whitespace-pre-wrap'>
        {text}
      </pre>
      <div className='mt-2'>
        <Button
          color={ButtonColor.SECONDARY}
          onClick={() => {
            navigator.clipboard?.writeText(text).then(
              () => {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              },
              () => {}
            )
          }}
        >
          {copied ? 'コピーしました' : 'コピー'}
        </Button>
      </div>
    </div>
  )
}

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className='border-border-input flex flex-wrap items-center justify-between gap-3 border-t py-3 first:border-t-0'>
    <Text size={13} weight='bold'>
      {label}
    </Text>
    <div className='min-w-0 text-right break-all'>{children}</div>
  </div>
)

const EndpointRow = ({ e }: { e: TagEndpointStatus }) => {
  const last = dt(e.last_request_at)
  return (
    <div className='border-border-input flex flex-wrap items-center gap-x-3 gap-y-1 border-t py-2 first:border-t-0'>
      <div className='min-w-0 flex-1'>
        <Text size={13}>{e.label}</Text>
        <div className='truncate'>
          <code className='text-[11px]'>
            {e.method} {e.path}
          </code>
        </div>
      </div>
      {last ? (
        <Text size={12} color={TextColor.SUCCESS}>
          受信済 {last}
        </Text>
      ) : (
        <Text size={12} color={TextColor.SECONDARY}>
          未受信
        </Text>
      )}
    </div>
  )
}

const SearchTagDeliveryPage = () => {
  const siteId = useSiteId()
  const { data, isLoading, mutate } = useTagDeliveryGetApi({ site_id: siteId })
  const showSaved = useShowSavedToast({ editing: true })
  // 発行直後の平文トークン。保存していないので、この画面を離れると二度と表示できない
  const [issuedToken, setIssuedToken] = useState<string | null>(null)
  const [fmt, setFmt] = useState<string | null>(null)

  const { trigger: issue, isMutating: issuing } = useTagDeliveryTokenIssueApi({
    onSuccess: (res) => {
      setIssuedToken(res.api_token)
      mutate()
    }
  })
  const { trigger: update, isMutating: updating } = useTagDeliveryUpdateApi({
    onSuccess: () => {
      showSaved('配信設定')
      setFmt(null)
      mutate()
    }
  })

  const fmtVal = fmt ?? data?.tag_page_url_format ?? ''

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='利用設定（API/配信）'
        description='w2Commerce と接続するための情報・設置手順・疎通状況をまとめています。'
      />
      <Content>
        {isLoading || !data ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : (
          <>
            {!data.site_enabled && (
              <div className='bg-surface-warning border-border-input rounded-xl border p-4'>
                <Text size={14} weight='bold'>
                  Tag Trail が無効です
                </Text>
                <div className='mt-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    この状態では互換APIも配信も停止します。サイト管理の機能有効化で「Tag
                    Trail」をONにしてください。
                  </Text>
                </div>
                <div className='mt-2'>
                  <Button color={ButtonColor.SECONDARY} link={siteEditPath(siteId)}>
                    サイト管理を開く
                  </Button>
                </div>
              </div>
            )}

            {issuedToken && (
              <div className='bg-surface-warning border-border-input rounded-xl border p-4'>
                <Text size={14} weight='bold'>
                  APIトークンを発行しました（この画面を離れると再表示できません）
                </Text>
                <div className='mt-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    トークンはハッシュのみ保存しているため、平文を後から確認することはできません。w2Commerce
                    側に設定してから画面を離れてください。
                  </Text>
                </div>
                <div className='mt-2'>
                  <CopyBlock text={issuedToken} />
                </div>
              </div>
            )}

            <Area>
              <Heading>接続情報</Heading>
              <AreaContent>
                <Text size={12} color={TextColor.SECONDARY}>
                  w2Commerce の awoo
                  連携設定に、下記の「呼び先ベースURL」と「認証情報」を設定します。URL構造・認証方式は
                  awoo と同じなので、差し替えるだけで切り替わります。
                </Text>
                <div className='mt-2'>
                  <InfoRow label='nununiId'>
                    {data.nununi_id ? (
                      <code className='text-[13px]'>{data.nununi_id}</code>
                    ) : (
                      <Text size={12} color={TextColor.SECONDARY}>
                        未発行（トークン発行時に採番されます）
                      </Text>
                    )}
                  </InfoRow>
                  <InfoRow label='呼び先ベースURL'>
                    <code className='text-[13px]'>{data.api_base_url}</code>
                  </InfoRow>
                  <InfoRow label='APIトークン'>
                    {data.token_issued ? (
                      <Text size={13}>••••••••{data.token_hint}</Text>
                    ) : (
                      <Text size={12} color={TextColor.SECONDARY}>
                        未発行
                      </Text>
                    )}
                  </InfoRow>
                  <InfoRow label='互換APIの配信'>
                    <div className='flex items-center gap-2'>
                      <Text
                        size={12}
                        color={data.enabled ? TextColor.SUCCESS : TextColor.SECONDARY}
                      >
                        {data.enabled ? '有効' : '無効'}
                      </Text>
                      <CheckBox
                        type={CheckBoxType.TOGGLE_BUTTON}
                        name='delivery_enabled'
                        checked={data.enabled}
                        onChange={(v) =>
                          data.token_issued && !updating && update({ site_id: siteId, enabled: v })
                        }
                      />
                    </div>
                  </InfoRow>
                </div>
                <div className='mt-3 flex items-center gap-3'>
                  <Button
                    color={ButtonColor.PRIMARY}
                    loading={issuing}
                    onClick={() => issue({ site_id: siteId })}
                  >
                    {data.token_issued ? 'トークンを再発行' : 'トークンを発行'}
                  </Button>
                  {data.token_issued && (
                    <Text size={12} color={TextColor.SECONDARY}>
                      再発行すると現在のトークンは即座に無効になります（w2Commerce
                      側の設定も差し替えてください）。
                    </Text>
                  )}
                </div>
              </AreaContent>
            </Area>

            <Area>
              <Heading>タグページURLの形式</Heading>
              <AreaContent>
                <Text size={12} color={TextColor.SECONDARY}>
                  タグから遷移する自社のタグページURLです。
                  <code className='text-[12px]'>{'{tags}'}</code>{' '}
                  にタグ（複数はカンマ区切り）が入ります。2ページ目以降は{' '}
                  <code className='text-[12px]'>pno</code> が付与されます。
                </Text>
                <div className='mt-2 flex flex-wrap items-center gap-2'>
                  <input
                    className={`${textInput} min-w-[360px] flex-1`}
                    value={fmtVal}
                    placeholder='/Form/Product/RecommendProductsList.aspx?tags={tags}'
                    onChange={(e) => setFmt(e.target.value)}
                  />
                  <Button
                    color={ButtonColor.PRIMARY}
                    loading={updating}
                    disabled={fmt === null || fmtVal === data.tag_page_url_format}
                    onClick={() => update({ site_id: siteId, tag_page_url_format: fmtVal })}
                  >
                    保存
                  </Button>
                </div>
                <div className='mt-2'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    現在の出力例: <code className='text-[12px]'>{data.tag_page_url_example}</code>
                  </Text>
                </div>
              </AreaContent>
            </Area>

            <Area>
              <Heading>疎通ステータス</Heading>
              <AreaContent>
                <Text size={12} color={TextColor.SECONDARY}>
                  タグの<b>描画</b>は w2Commerce（互換APIを呼ぶ）、<b>計測</b>は w2search.js
                  と担い手が分かれます。両方の受信が揃って初めてダッシュボードの数値が出ます。
                </Text>

                <div className='mt-3'>
                  <Text size={13} weight='bold'>
                    ① 互換API（w2Commerce からの呼び出し）
                  </Text>
                  <div className='mt-1'>
                    {data.endpoints.map((e) => (
                      <EndpointRow key={e.key} e={e} />
                    ))}
                  </div>
                </div>

                <div className='mt-4'>
                  <Text size={13} weight='bold'>
                    ② 計測イベント（w2search.js からの送信）
                  </Text>
                  <div className='mt-1 flex items-center gap-3'>
                    <Text
                      size={13}
                      color={
                        data.measurement_status === 'active'
                          ? TextColor.SUCCESS
                          : TextColor.SECONDARY
                      }
                    >
                      {MEASUREMENT_TEXT[data.measurement_status]}
                    </Text>
                    <Text size={12} color={TextColor.SECONDARY}>
                      最終受信: {dt(data.measurement_last_event_at) ?? 'なし'}
                    </Text>
                  </div>
                </div>
              </AreaContent>
            </Area>

            <Area>
              <Heading>計測タグの設置</Heading>
              <AreaContent>
                <Text size={12} color={TextColor.SECONDARY}>
                  検索サジェストと共通の1本です。すでに設置済みなら追加の設置は不要です（設置先は
                  &lt;head&gt; 内推奨）。
                </Text>
                <div className='mt-2'>
                  <CopyBlock text={data.embed_snippet} label='共通タグ（全ページ）' />
                </div>
                <div className='mt-4'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    注文完了（サンクス）ページに設置すると、タグ経由の購入を計測できます。
                    <code className='text-[12px]'>{'{{注文番号}}'}</code>{' '}
                    等は各サイトのテンプレート変数に置き換えてください。
                  </Text>
                  <div className='mt-2'>
                    <CopyBlock text={data.cv_snippet} label='CV計測タグ（注文完了ページ）' />
                  </div>
                </div>
              </AreaContent>
            </Area>

            <Area>
              <Heading>計測の発火（w2Commerce 側の描画コードから呼ぶ）</Heading>
              <AreaContent>
                <Text size={12} color={TextColor.SECONDARY}>
                  タグの描画は w2Commerce
                  側で行うため、下記の関数を描画コードから呼んでください。0件のときも呼ぶことが重要です（呼ばないと「表示0件」と「未計測」が区別できません）。
                </Text>
                <div className='mt-2'>
                  <CopyBlock
                    text={`// 商品詳細でタグ枠を描画したとき（0件でも呼ぶ）
window.w2sTag.viewTag([12, 34], { customerId: '会員ID（任意）' });

// タグがクリックされたとき
window.w2sTag.clickTag(12, { customerId: '会員ID（任意）' });

// タグページを表示したとき（商品0件でも呼ぶ）
window.w2sTag.viewTagPage([12, 34], 24);

// タグページの商品カードがクリックされたとき
window.w2sTag.clickProduct('商品ID', { position: 1 });`}
                  />
                </div>
                <div className='mt-2'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    ※ 購入は上のCV計測タグが自動で送信するため、個別に呼ぶ必要はありません。
                  </Text>
                </div>
              </AreaContent>
            </Area>
          </>
        )}
      </Content>
    </Main>
  )
}

export default SearchTagDeliveryPage
